import { useEffect, useState } from 'react'

import { t } from '../../../../../common/i18n.js'
import type {
    AssetLookupPayload,
    MyAssetsPayload,
    PortalAsset,
} from '../../../../../common/types/assets.js'
import type { Language } from '../../../../../common/types/i18n.js'
import { formatDisplayDate } from '../../../../../common/utils/format-utils.js'
import { Button } from '../../../../components/button/button.js'
import { TextField } from '../../../../components/forms/text-field/text-field.js'
import { Archive } from '../../../../components/icon/solar-icons.js'
import {
    PortalDataTable,
    type PortalDataTableColumn,
    type SortState,
} from '../../../../components/portal-data-table/portal-data-table.js'
import { PortalEmptyState } from '../../../../components/portal-empty-state/portal-empty-state.js'
import { Skeleton } from '../../../../components/skeleton/skeleton.js'
import { Spinner } from '../../../../components/spinner/spinner.js'
import { portalGet, portalPost } from '../../utils/api.js'
import './assets-view.css'

interface AssetsViewProps {
    lang: Language
    showToast?: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void
}

type LookupState =
    | { phase: 'idle' }
    | { phase: 'searching' }
    | { phase: 'result'; payload: AssetLookupPayload }

// "My assets" (/me/assets): the person's items from the asset-management
// system (asset.playnope.com), matched by email, plus claim-by-tag. The asset
// system stays the source of truth — this view never edits asset data beyond
// the checkout the claim performs.
export const AssetsView = ({ lang }: AssetsViewProps) => {
    const [payload, setPayload] = useState<MyAssetsPayload | null>(null)
    const [loading, setLoading] = useState(true)
    const [tag, setTag] = useState('')
    const [lookup, setLookup] = useState<LookupState>({ phase: 'idle' })
    const [claiming, setClaiming] = useState(false)
    const [claimError, setClaimError] = useState<string | null>(null)
    const [claimedTag, setClaimedTag] = useState<string | null>(null)
    const [sort, setSort] = useState<SortState | undefined>(undefined)

    const a = t.portal.assets

    const reload = async () => {
        const result = await portalGet<MyAssetsPayload>('/api/portal/me/assets')
        if (result.ok) setPayload(result.data)
        setLoading(false)
    }

    useEffect(() => {
        void reload()
    }, [])

    const handleSearch = async () => {
        const trimmed = tag.trim()
        if (!trimmed) return
        setClaimedTag(null)
        setClaimError(null)
        setLookup({ phase: 'searching' })
        const result = await portalGet<AssetLookupPayload>(
            `/api/portal/me/assets/lookup?tag=${encodeURIComponent(trimmed)}`,
        )
        setLookup({
            phase: 'result',
            payload: result.ok ? result.data : { status: 'error' },
        })
    }

    const handleClaim = async (asset: PortalAsset) => {
        setClaiming(true)
        setClaimError(null)
        try {
            const result = await portalPost<{ ok: boolean }>('/api/portal/me/assets/claim', {
                tag: asset.tag,
            })
            if (!result.ok) {
                setClaimError(result.error)
                return
            }
            setClaimedTag(asset.tag)
            setLookup({ phase: 'idle' })
            setTag('')
            await reload()
        } finally {
            setClaiming(false)
        }
    }

    if (loading || !payload) {
        return (
            <div className="portal-assets__skeleton">
                <Skeleton count={6} height={44} />
            </div>
        )
    }

    if (payload.status === 'not_configured') {
        return <PortalEmptyState icon={<Archive size={32} />} message={a.notConfigured(lang)} />
    }
    if (payload.status === 'error') {
        return <PortalEmptyState icon={<Archive size={32} />} message={a.error(lang)} />
    }

    const columns: PortalDataTableColumn<PortalAsset>[] = [
        {
            key: 'name',
            header: a.colName(lang),
            truncate: true,
            sortable: true,
            render: (asset) => asset.name ?? asset.model ?? asset.tag,
        },
        {
            key: 'tag',
            header: a.colTag(lang),
            sortable: true,
            render: (asset) => (
                <a
                    className="portal-assets__tag-link"
                    href={asset.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={a.openInAssetSystem(lang)}
                >
                    {asset.tag}
                </a>
            ),
        },
        {
            key: 'model',
            header: a.colModel(lang),
            sortable: true,
            render: (asset) => asset.model ?? '—',
        },
        {
            key: 'serial',
            header: a.colSerial(lang),
            sortable: true,
            render: (asset) => asset.serial ?? '—',
        },
        {
            key: 'since',
            header: a.colSince(lang),
            sortable: true,
            render: (asset) =>
                asset.checkedOutAt
                    ? formatDisplayDate(asset.checkedOutAt.slice(0, 10), 'short', lang)
                    : '—',
        },
    ]

    // Sort key per column. The 'since' column sorts by the raw ISO date (which
    // orders chronologically as a string); the rest sort case-insensitively.
    const sortValue = (asset: PortalAsset, key: string): string => {
        switch (key) {
            case 'name':
                return (asset.name ?? asset.model ?? asset.tag).toLowerCase()
            case 'tag':
                return asset.tag.toLowerCase()
            case 'model':
                return (asset.model ?? '').toLowerCase()
            case 'serial':
                return (asset.serial ?? '').toLowerCase()
            case 'since':
                return asset.checkedOutAt ?? ''
            default:
                return ''
        }
    }

    const sortAssets = (assets: PortalAsset[]): PortalAsset[] => {
        if (!sort) return assets
        return [...assets].sort((x, y) => {
            const cmp = sortValue(x, sort.key).localeCompare(sortValue(y, sort.key), undefined, {
                numeric: true,
            })
            return sort.direction === 'asc' ? cmp : -cmp
        })
    }

    const lookupResult = lookup.phase === 'result' ? lookup.payload : null

    return (
        <div className="portal-assets">
            {payload.status === 'no_account' ? (
                <PortalEmptyState icon={<Archive size={32} />} message={a.noAccount(lang)} />
            ) : payload.assets.length === 0 ? (
                <PortalEmptyState icon={<Archive size={32} />} message={a.empty(lang)} />
            ) : (
                <PortalDataTable<PortalAsset>
                    columns={columns}
                    rows={sortAssets(payload.assets)}
                    rowKey={(asset) => asset.id}
                    sort={sort}
                    onSortChange={setSort}
                />
            )}

            {payload.status === 'connected' && (
                <section className="portal-assets__claim">
                    <h3 className="portal-assets__claim-title">{a.claimTitle(lang)}</h3>
                    <p className="portal-assets__claim-description">{a.claimDescription(lang)}</p>
                    <div className="portal-assets__claim-form">
                        <TextField
                            className="portal-assets__claim-input"
                            value={tag}
                            placeholder={a.claimPlaceholder(lang)}
                            onChange={(value) => setTag(value)}
                            onEnter={() => void handleSearch()}
                        />
                        <Button
                            size="small"
                            variant="secondary"
                            text={a.claimSearch(lang)}
                            isDisabled={lookup.phase === 'searching' || !tag.trim()}
                            onClick={() => void handleSearch()}
                        />
                    </div>

                    {lookup.phase === 'searching' && <Spinner />}

                    {lookupResult?.status === 'not_found' && (
                        <p className="portal-assets__claim-note">{a.lookupNotFound(lang)}</p>
                    )}
                    {lookupResult?.status === 'error' && (
                        <p className="portal-assets__claim-note">{a.error(lang)}</p>
                    )}
                    {lookupResult?.status === 'found' && (
                        <>
                            {lookupResult.assets.length > 1 && (
                                <p className="portal-assets__claim-note">
                                    {a.lookupMultiple(lang)}
                                </p>
                            )}
                            {lookupResult.assets.map((asset) => (
                                <div className="portal-assets__claim-result" key={asset.id}>
                                    <div className="portal-assets__claim-asset">
                                        <span className="portal-assets__claim-asset-name">
                                            {asset.name ?? asset.model ?? asset.tag}
                                        </span>
                                        <span className="portal-assets__claim-asset-meta">
                                            {[asset.tag, asset.model, asset.status]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        </span>
                                    </div>
                                    {asset.available ? (
                                        <Button
                                            size="small"
                                            text={a.claimButton(lang)}
                                            isDisabled={claiming}
                                            onClick={() => void handleClaim(asset)}
                                        />
                                    ) : (
                                        <span className="portal-assets__claim-note">
                                            {asset.assignedTo
                                                ? a.lookupTakenBy(asset.assignedTo)(lang)
                                                : a.lookupTaken(lang)}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </>
                    )}

                    {claimError && <p className="portal-assets__claim-error">{claimError}</p>}
                    {claimedTag && (
                        <p className="portal-assets__claim-success">
                            {a.claimSuccess(lang)} ({claimedTag})
                        </p>
                    )}
                </section>
            )}
        </div>
    )
}
